import React from 'react';
import { Tooltip } from 'antd';

import TagBadge, { TagRow } from './TagBadge';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { entryName, removeLabelFor } from '../../utils/itemTags';
import { ACTIVE_TAG_NOTE } from '../../utils/activeTag';
import { normalizeId } from '../../utils/ids';
import { tagPagePath } from '../../utils/tags';

/**
 * THE TAGS ON ONE ITEM, as chips (docs/plans/tags.md §5.1, §5.2;
 * docs/platform-standards.md § Tags). The entries come from
 * utils/itemTags.js `itemTagEntries` — collection and item tags, then the
 * reader's private tags, then the private tags the item's collection
 * carries — and each is a TagBadge:
 *
 * - a rule's application wears the automation glyph;
 * - a private tag (or a private use of an item tag) the muted fill and the
 *   user glyph; one the collection carries says "From the collection";
 * - an item tag is its item's badge (the card's tile, the item's title),
 *   qualified outside the collection its item lives in; one the reader
 *   cannot read is a tombstone.
 *
 * The ✕ appears where `canRemove(entry)` says the reader may take the chip
 * off (utils/itemTags.js canRemoveEntry) and `onRemove` is given — read-only
 * in a table cell and on a tile, where no ✕ is offered at all.
 *
 * `explainFor(entry)` (the read view, T6a) → a function drawing why a rule
 * applied that chip, or nothing: its automation glyph then opens "Why".
 *
 * An entry marked `active` (a new item's form: the person's ACTIVE TAG,
 * docs/plans/tags-active-tag-proposal.md) says "Your active tag" beside its
 * pill, and its ✕ leaves it off that one item.
 *
 * `max` shows the first N and then "+N" (a tile shows three). Rows wrap and
 * never scroll sideways — except where `singleLine` asks for one line (the
 * tile's fixed height), which clips instead.
 *
 * `linked` makes each chip the door to its TAG PAGE (`/tags/:id`, T5) — where
 * a click on a tag means "show me everything with it": the read view's row
 * and a table cell. Never on a tile (the tile is itself a click target) or in
 * the item form (a click would leave the form). A tombstone, a neutral chip
 * and a tag the API says is not the reader's to open (`openable: false`,
 * keepr-api b74) stay inert: their page would be the one 404. `isolateClicks` keeps a
 * linked chip's click from reaching what holds the row (a table row selects
 * its item on a click; the chip's click is the chip's alone).
 *
 * `cardsById` names an item tag's card where the definitions cache can't —
 * the public page, whose cards arrive in its own payload (T7).
 */
export default function ItemTagChips({
	entries = [],
	onRemove,
	canRemove,
	max = null,
	size = 'xs',
	label = 'Tags',
	className = '',
	singleLine = false,
	empty = null,
	disabled = false,
	linked = false,
	isolateClicks = false,
	explainFor,
	cardsById: cardsByIdProp = null,
}) {
	const cards = useOptionalCardDefinitions();
	const cardsById = cardsByIdProp || cards?.cardsById || null;
	const list = Array.isArray(entries) ? entries : [];
	if (!list.length) return empty;

	const shown = Number.isInteger(max) && max >= 0 && list.length > max ? list.slice(0, max) : list;
	const hidden = list.length - shown.length;

	const resolveItemTag = tag => {
		if (!tag || tag.kind !== 'item') return null;
		const card = cardsById ? cardsById[normalizeId(tag.card_id)] || null : null;
		return {
			record: card,
			title: tag.title || 'Untitled item',
			collection: tag.collection_id || null,
		};
	};

	const renderTag = entry => {
		const removable = typeof onRemove === 'function' && !disabled && (typeof canRemove === 'function' ? canRemove(entry) : true);
		const to = linked && !entry.tombstone ? tagPagePath(entry.tag) : null;
		const chip = (
			<TagBadge
				tag={entry.tombstone ? null : entry.tag}
				size={size}
				to={to || undefined}
				automatic={entry.automatic}
				inherited={entry.inherited}
				privateUse={entry.private && entry.source === 'item'}
				resolveItemTag={resolveItemTag}
				explain={entry.automatic && typeof explainFor === 'function' ? explainFor(entry) : undefined}
				note={entry.active ? ACTIVE_TAG_NOTE : undefined}
				onRemove={removable ? () => onRemove(entry) : undefined}
				removeLabel={removable ? removeLabelFor(entry) : undefined}
				data-entry={entry.key}
			/>
		);
		if (!to || !isolateClicks) return chip;
		return (
			<span className="item-tag-chips__isolate" onClick={event => event.stopPropagation()} role="presentation">
				{chip}
			</span>
		);
	};

	const classes = ['item-tag-chips', singleLine ? 'item-tag-chips--single-line' : '', className].filter(Boolean).join(' ');
	return (
		<div className={classes}>
			<TagRow tags={shown} label={label} renderTag={renderTag} />
			{hidden > 0 ? (
				<Tooltip title={list.slice(shown.length).map(entry => entryName(entry) || 'No longer available').join(', ')}>
					<span className="item-tag-chips__more" aria-label={`${hidden} more ${hidden === 1 ? 'tag' : 'tags'}`}>
						+{hidden}
					</span>
				</Tooltip>
			) : null}
		</div>
	);
}
